'use client'

import { Button } from '@/src/components/common/button'
import { useEffect } from 'react'

/**
 * Marks a browser that has already been reloaded for a stale build, so a bug
 * that survives the reload shows the error instead of reloading forever.
 */
const RELOADED_KEY = 'lunava:stale-deployment-reload'

/**
 * A server action the running server does not know about.
 *
 * The action id is baked into the client bundle at build time, so a tab opened
 * before a deployment keeps calling ids the new build never emitted. The
 * server logs `Failed to find Server Action "…"` and the call rejects here.
 * Nothing about the session is wrong; the page is simply out of date, and
 * fetching it again resolves it.
 */
function isStaleDeployment(error: Error): boolean {
  return /Failed to find Server Action/i.test(error.message)
}

export default function AppError({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
  const stale = isStaleDeployment(error)

  useEffect(() => {
    if (!stale) return

    if (window.sessionStorage.getItem(RELOADED_KEY)) return

    window.sessionStorage.setItem(RELOADED_KEY, '1')
    window.location.reload()
  }, [stale])

  useEffect(() => {
    if (!stale) console.error(error)
  }, [error, stale])

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-8 text-center">
      <h1 className="text-lg font-semibold">{stale ? 'This page is out of date' : 'Something went wrong'}</h1>
      <p className="max-w-md text-sm text-muted-foreground">
        {stale
          ? 'A newer version of Lunava has been released. Reloading picks it up.'
          : 'The page could not be loaded. Trying again often works; if it does not, the error has been logged.'}
      </p>
      <div className="flex gap-2">
        <Button onClick={() => window.location.reload()}>Reload</Button>
        {!stale && (
          <Button outline onClick={reset}>
            Try again
          </Button>
        )}
      </div>
    </div>
  )
}
